import { CheckCircleIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useMemo, useState } from "react";
import {
  FaAdjust,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaChevronRight,
  FaExclamationCircle,
  FaHourglass,
  FaMoneyBill,
  FaStar,
  FaTimesCircle,
} from "react-icons/fa";
import { useListReceivedBidsQuery } from "../../../../../../autogen/bff-api";
import { Stat } from "../../../../../../common/Stat";
import { ProductGroup } from "../../../edit-negotiation/Products";
import { useNegotiation } from "../../NegotiationContext";
import { useAIEvaluation } from "./useAIEvaluation";
import { useRecommendationTag } from "./useRecommendationTag";

export const AIEvaluator = ({
  sourcingEventId,
  productGroups,
  setOrgIdToView,
}: {
  sourcingEventId: string;
  productGroups: ProductGroup[];
  setOrgIdToView: (id: string) => void;
}) => {
  const { currentRound } = useNegotiation();

  const getRecommendationTag = useRecommendationTag();

  const { data: bidsResponse } = useListReceivedBidsQuery({
    eventId: sourcingEventId,
    negotiationRound: currentRound?.id,
  });

  const bids = useMemo(() => bidsResponse?.bids ?? [], [bidsResponse?.bids]);

  const [isComparisonExpanded, setIsComparisonExpanded] = useState(false);

  const getBidderName = (bidId: string): string => {
    const bid = bids.find((b) => b.id === bidId);
    return bid?.owningOrganization?.name ?? t("Not found");
  };

  const getFulfillmentIcon = (fulfilled: "yes" | "no" | "partly") => {
    switch (fulfilled) {
      case "yes":
        return FaCheck;
      case "partly":
        return FaExclamationCircle;
      case "no":
        return FaTimesCircle;
    }
  };

  const getFulfillmentColor = (fulfilled: "yes" | "no" | "partly") => {
    switch (fulfilled) {
      case "yes":
        return "smSuccess";
      case "partly":
        return "smWarning";
      case "no":
        return "smDanger";
    }
  };

  const getBestBidderScoreLabel = () => {
    if (!comparison) return t("The bid comparison is not complete");
    const bidEvaluation = individualEvaluations.find((evaluation) => evaluation.bidId === comparison.summary.bestBid);
    if (!bidEvaluation) return t("Score not found");
    return `${t("Score")}: ${bidEvaluation.score}/100`;
  };

  const getPriceRangeLabel = () => {
    const prices = individualEvaluations.map((evaluation) => evaluation.totalPrice);
    const lowestPrice = Math.min(...prices);
    const highestPrice = Math.max(...prices);
    return `${lowestPrice.toLocaleString()} - ${highestPrice.toLocaleString()}`;
  };

  const {
    requirements,
    individualEvaluations,
    comparison,
    isLoading,
    isGeneratingRequirements,
    isEvaluatingSingleBid,
    isComparingBids,
    error,
  } = useAIEvaluation({
    sourcingEventId,
    negotiationRoundId: currentRound?.id,
    bids,
    productGroups,
  });

  return (
    <VStack spacing={5} align="stretch">
      {error && (
        <Alert status="error">
          <AlertIcon />
          <Text>{`${t("Error evaluating bids with AI")}.`}</Text>
        </Alert>
      )}
      <VStack width="full" spacing="1" alignItems="start">
        {isGeneratingRequirements && (
          <HStack spacing={2}>
            <Spinner size="sm" color="smPrimary" />
            <Text color="smMuted" fontSize="sm">
              {`${t("Generating requirements")}...`}
            </Text>
          </HStack>
        )}
        {requirements && !isGeneratingRequirements && (
          <HStack spacing={2}>
            <Icon as={FaCheckCircle} color="smPrimary" />
            <Text color="smMuted" fontSize="sm">
              {`${t("Requirements ready")}.`}
            </Text>
          </HStack>
        )}
        {!isEvaluatingSingleBid && !individualEvaluations.length && (
          <HStack spacing={2}>
            <Icon as={FaHourglass} color="smInfo" />
            <Text color="smInfo" fontSize="sm">
              {`${t("Bid evaluations pending requirement generation")}.`}
            </Text>
          </HStack>
        )}
        {isEvaluatingSingleBid && (
          <HStack spacing={2}>
            <Spinner size="sm" color="smPrimary" />
            <Text color="smMuted" fontSize="sm">
              {`${t("Evaluating")} ${bids.length - individualEvaluations.length} ${t("out of")} ${bids.length} ${t("bids")}...`}
            </Text>
          </HStack>
        )}
        {individualEvaluations.length === bids.length && !isEvaluatingSingleBid && (
          <HStack spacing={2}>
            <Icon as={FaCheckCircle} color="smPrimary" />
            <Text color="smMuted" fontSize="sm">
              {`${individualEvaluations.length} ${t("out of")} ${bids.length} ${t("bids evaluated")}.`}
            </Text>
          </HStack>
        )}
        {!isComparingBids && !comparison && (
          <HStack spacing={2}>
            <Icon as={FaHourglass} color="smInfo" />
            <Text color="smInfo" fontSize="sm">
              {`${t("Bid comparison pending bid evaluation")}.`}
            </Text>
          </HStack>
        )}
        {isComparingBids && (
          <HStack spacing={2}>
            <Spinner size="sm" color="smPrimary" />
            <Text color="smMuted" fontSize="sm">
              {`${t("Running bid comparison")}...`}
            </Text>
          </HStack>
        )}
        {comparison && !isComparingBids && (
          <HStack spacing={2}>
            <Icon as={FaCheckCircle} color="smPrimary" />
            <Text color="smMuted" fontSize="sm">
              {`${t("Bid comparison complete")}.`}
            </Text>
          </HStack>
        )}
      </VStack>
      <Flex columnGap={5}>
        <Stat
          title={t("Bids evaluated")}
          values={[
            isLoading
              ? `${t("Loading")}...`
              : `${individualEvaluations.length} ${t("out of")} ${bids.length} ${t("bids evaluated with AI")}`,
          ]}
          icon={<Icon as={CheckCircleIcon} color="smPrimary" boxSize={8} />}
          color="smPrimary"
        />
        <Stat
          title={t("Best bidder")}
          values={
            comparison
              ? [
                  comparison?.summary.bestBid ? getBidderName(comparison.summary.bestBid) : t("None qualified"),
                  getBestBidderScoreLabel(),
                ]
              : [`${t("Loading")}...`]
          }
          icon={<Icon as={FaStar} color="smSuccess" boxSize={8} />}
          color="smSuccess"
        />
        <Stat
          title={t("Average score")}
          values={[
            individualEvaluations.length
              ? `${individualEvaluations.reduce((sum, evaluation) => sum + evaluation.score, 0) / individualEvaluations.length}/100`
              : t("No bid evaluations found"),
          ]}
          icon={<Icon as={FaAdjust} color="smWarning" boxSize={8} />}
          color="smWarning"
        />
        <Stat
          title={t("Price range")}
          values={[individualEvaluations.length ? getPriceRangeLabel() : t("No bid evaluations found")]}
          icon={<Icon as={FaMoneyBill} color="smSecondary" boxSize={8} />}
          color="smSecondary"
        />
      </Flex>
      {(comparison || individualEvaluations.length > 0) && (
        <Tabs display="flex" flexDirection="column" width="full" minHeight={0} variant="unstyled">
          <TabList bg="smBackground" py="2" display="flex" justifyContent="center">
            <Flex
              bg="smBackground"
              border="1px solid"
              borderColor="smBorder"
              rounded="full"
              p="0.5"
              gap="1"
              width="full"
            >
              <Tab
                flex={1}
                rounded="full"
                fontWeight="semibold"
                color="smMuted"
                textAlign="center"
                borderWidth="1px"
                borderColor="transparent"
                _selected={{ bg: "smBackgroundSecondary", color: "smPrimary", borderColor: "transparent" }}
                _hover={{ bg: "smBackgroundSecondary", opacity: 0.9 }}
                px="3"
                py="0.5"
              >
                {t("Overview")}
              </Tab>
              <Tab
                flex={1}
                rounded="full"
                fontWeight="semibold"
                color="smMuted"
                textAlign="center"
                borderWidth="1px"
                borderColor="transparent"
                _selected={{ bg: "smBackgroundSecondary", color: "smPrimary", borderColor: "transparent" }}
                _hover={{ bg: "smBackgroundSecondary", opacity: 0.9 }}
                px="3"
                py="0.5"
              >
                {t("Requirements")}
              </Tab>
            </Flex>
          </TabList>
          <TabPanels>
            <TabPanel px="0">
              <VStack spacing={5} align="stretch">
                <Flex border="1px solid" borderColor="smBorder" rounded="lg" pb="2.5">
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>{t("Bidder")}</Th>
                        <Th>{t("Bid Amount")}</Th>
                        <Th>{t("Overall Score")}</Th>
                        <Th>{t("Requirements Compliance")}</Th>
                        <Th>{t("Recommendation")}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {individualEvaluations
                        .sort((a, b) => b.score - a.score)
                        .map((evaluation) => (
                          <Tr
                            key={evaluation.bidId}
                            cursor="pointer"
                            _hover={{ backgroundColor: "smBackgroundSecondary" }}
                            onClick={() => {
                              const orgId = bids.find((bid) => bid.id === evaluation.bidId)?.owningOrganization.id;
                              if (!orgId) throw Error("Bid not found - could not open view bid as buyer modal");
                              setOrgIdToView(orgId);
                            }}
                          >
                            <Td>{getBidderName(evaluation.bidId)}</Td>
                            <Td>{evaluation.totalPrice.toLocaleString()}</Td>
                            <Td
                              color={
                                evaluation.score > 80 ? "smSuccess" : evaluation.score > 60 ? "smWarning" : "smDanger"
                              }
                            >
                              {evaluation.score}/100
                            </Td>
                            <Td>
                              <Flex alignItems="center" columnGap="1">
                                <Icon as={FaCheckCircle} color="smSuccess" />
                                <Text as="span" color="smSuccess" mr="2">
                                  {evaluation.requirements.filter((r) => r.fulfilled === "yes").length}
                                </Text>
                                <Icon as={FaExclamationCircle} color="smWarning" />
                                <Text as="span" color="smWarning" mr="2">
                                  {evaluation.requirements.filter((r) => r.fulfilled === "partly").length}
                                </Text>
                                <Icon as={FaTimesCircle} color="smDanger" />
                                <Text as="span" color="smDanger" mr="2">
                                  {evaluation.requirements.filter((r) => r.fulfilled === "no").length}
                                </Text>
                              </Flex>
                            </Td>
                            <Td>{getRecommendationTag(evaluation.recommendation)}</Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </Flex>
                {comparison && (
                  <Box
                    position="relative"
                    backgroundColor="smSecondarySoft"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="smBorder"
                    maxHeight={isComparisonExpanded ? "" : "32"}
                    overflow="hidden"
                    p="4"
                  >
                    <VStack spacing={3} align="stretch">
                      <Box>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Text fontWeight="semibold" mb={1}>{`${t("Executive Summary")}`}</Text>
                          <IconButton
                            variant="unstyled"
                            aria-label="expand comparison"
                            icon={<Icon as={isComparisonExpanded ? FaChevronRight : FaChevronDown} />}
                            onClick={() => setIsComparisonExpanded((isExpanded) => !isExpanded)}
                          />
                        </Flex>
                        <Text fontSize="sm" wordBreak="break-word" whiteSpace="normal">
                          {comparison.summary.executiveSummary}
                        </Text>
                      </Box>
                      <Divider />
                      <Box>
                        <Text fontWeight="semibold" mb={2}>{`${t("Key Insights")}:`}</Text>
                        <VStack spacing={1} align="stretch">
                          {comparison.summary.keyInsights.map((insight, index) => (
                            <Text key={index} fontSize="sm" pl={2}>
                              • {insight}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                      <Divider />
                      <Box>
                        <Text fontWeight="semibold" mb={1}>{`${t("Overall Recommendation")}`}</Text>
                        <Text fontSize="sm" wordBreak="break-word" whiteSpace="normal">
                          {comparison.summary.overallRecommendation}
                        </Text>
                      </Box>
                    </VStack>
                    <Box
                      position="absolute"
                      display={isComparisonExpanded ? "none" : ""}
                      width="full"
                      height="full"
                      top="10"
                      bottom="0"
                      left="0"
                      bgGradient="linear(to bottom, transparent, smSecondarySoft)"
                      pointerEvents="none"
                    />
                  </Box>
                )}
              </VStack>
            </TabPanel>
            <TabPanel px="0">
              {individualEvaluations.length > 0 && (
                <Box border="1px solid" borderColor="smBorder" rounded="lg" pb="2.5">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>{t("Requirement")}</Th>
                        {individualEvaluations.map((evaluation) => (
                          <Th key={evaluation.bidId} textAlign="center">
                            {getBidderName(evaluation.bidId)}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {requirements?.map((req: string) => (
                        <Tr key={req}>
                          <Td fontSize="sm">{req}</Td>
                          {individualEvaluations.map((bidEvaluation) => {
                            const requirementFulfillment = bidEvaluation.requirements.find(
                              (r) => r.requirement === req
                            );
                            return (
                              <Td key={bidEvaluation.bidId} textAlign="center">
                                {requirementFulfillment ? (
                                  <Icon
                                    as={getFulfillmentIcon(requirementFulfillment.fulfilled)}
                                    color={getFulfillmentColor(requirementFulfillment.fulfilled)}
                                    boxSize={4}
                                  />
                                ) : null}
                              </Td>
                            );
                          })}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </VStack>
  );
};
